import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';

// Error codes
export const ERROR_CODES = {
  UNAUTHORIZED: 'AUTH_001',
  FORBIDDEN: 'AUTH_002',
  NOT_FOUND: 'RESOURCE_001',
  VALIDATION_ERROR: 'VALIDATION_001',
  NETWORK_ERROR: 'NETWORK_001',
  TIMEOUT: 'TIMEOUT_001',
  CONFLICT: 'CONFLICT_001',
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_001',
  INTERNAL_SERVER_ERROR: 'SERVER_001',
  SERVICE_UNAVAILABLE: 'SERVICE_001',
  BAD_GATEWAY: 'GATEWAY_001',
} as const;

export type ErrorCode = keyof typeof ERROR_CODES;

export class AppError extends Error {
  constructor(
    message: string,
    public code: string,
    public statusCode: number = 500,
    public details?: unknown,
    public isOperational: boolean = true
  ) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = 'AppError';
    Error.captureStackTrace?.(this, this.constructor);
  }
}

export const handleError = (error: unknown): AppError => {
  console.error('Error:', error);
  
  if (error instanceof AppError) {
    return error;
  }
  
  if (error instanceof Error) {
    return new AppError(error.message, 'UNKNOWN_ERROR', 500, error, false);
  }
  
  const errorMessage = typeof error === 'string' 
    ? error 
    : 'An unknown error occurred';
    
  return new AppError(errorMessage, 'UNKNOWN_ERROR', 500, { original: error }, false);
};

// Error boundary props type
interface IErrorBoundaryProps {
  children: ReactNode;
  fallback: ReactNode | ((error: Error, reset: () => void) => ReactNode);
  onError?: (error: Error, info: ErrorInfo) => void;
  onReset?: () => void;
}

// Error boundary state type
interface IErrorBoundaryState {
  error: Error | null;
}

// Error boundary component
export class ErrorBoundary extends Component<IErrorBoundaryProps, IErrorBoundaryState> {
  public state: IErrorBoundaryState = { error: null };

  public static getDerivedStateFromError(error: Error): IErrorBoundaryState {
    return { error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Error boundary caught an error:', error, errorInfo);
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    } else {
      Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
    }
  }

  private resetError = (): void => {
    if (this.props.onReset) {
      this.props.onReset();
    }
    this.setState({ error: null });
  };

  public render(): ReactNode {
    if (this.state.error) {
      const { fallback } = this.props;
      const { error } = this.state;
      
      if (typeof fallback === 'function') {
        return fallback(error, this.resetError);
      }
      return fallback;
    }

    return this.props.children;
  }
} 